import { useEffect, useRef, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import Notifications from '@/components/Notifications'
import BrowserEnrollmentAlertBanner from '@/common/BrowserEnrollmentAlertBanner.jsx'
import HibuSecurityBlocker from '@/common/HibuSecurityBlocker.jsx'
import MobileNav from '@/components/MobileNav'
import Sidebar from '@/components/Sidebar/index'
import PAGES from '@/enums/pages'
import useBrowserEnrollment from '@/hooks/useBrowserEnrollment/useBrowserEnrollment.js'
import { getPageTitle } from '@/utils/common'
import ScrollingBanner from '@/widgets/ScrollingBanner'
import { Toaster } from '@/components/ui/toaster'
import { useDisclosure } from '@/hooks/useDisclosure'
import { cn } from '@/lib/utils'

const DefaultLayout = () => {
    const { isPermanentEidAccepted, isTemporaryEidAccepted, isBannerEnabled } =
        useBrowserEnrollment()
    const { isOpen, onOpen, onClose } = useDisclosure()
    const [activePage, setActivePage] = useState(PAGES.HOME.slug)
    const location = useLocation()
    const isHomePage = location.pathname === '/'
    const pageTitle = getPageTitle(activePage)
    const isLivebooks =
        location.pathname.includes('/livebooks') ||
        location.pathname.includes('/livebooks-legacy')
    const isAttendance = location.pathname.includes('/attendance')

    const [bannerHeight, setBannerHeight] = useState(0)
    const bannerRef = useRef(null)
    const widgetRef = useRef(null)
    const [widgetMargin, setWidgetMargin] = useState('0px')

    useEffect(() => {
        const updateBannerHeight = () => {
            if (bannerRef.current) {
                const bannerHeight =
                    bannerRef.current.getBoundingClientRect().height
                setBannerHeight(bannerHeight)
            }
        }

        // Update banner height on mount and resize
        setTimeout(updateBannerHeight, 80) // Temporary workaround for ref attachment
        window.addEventListener('resize', updateBannerHeight)

        return () => {
            window.removeEventListener('resize', updateBannerHeight)
        }
    }, [bannerRef])

    useEffect(() => {
        document.title = pageTitle
    }, [pageTitle])

    const handleVisibilityChange = visible => {
        setWidgetMargin(visible ? '50px' : '0px')
    }

    const isInternshipPage = location?.pathname === '/internships'
    const shouldShowBlocker = isBannerEnabled
    const shouldShowBanner =
        isBannerEnabled &&
        !isInternshipPage &&
        !isPermanentEidAccepted &&
        !isTemporaryEidAccepted

    return (
        <div
            className={cn(
                'bg-white dark:bg-gray-900 h-full transition-colors',
                isOpen ? 'overflow-x-hidden' : 'overflow-x-visible'
            )}
            id="layout-wrapper"
            style={{
                paddingTop: shouldShowBanner ? `${bannerHeight}px` : '0',
            }}
        >
            {shouldShowBlocker && <HibuSecurityBlocker />}
            {shouldShowBanner && (
                <BrowserEnrollmentAlertBanner
                    ref={bannerRef}
                    setBannerHeight={setBannerHeight}
                />
            )}
            {/* Scrolling Widget */}
            <div
                className="hidden md:block"
                style={{ marginBottom: widgetMargin }}
            >
                <ScrollingBanner
                    ref={widgetRef}
                    onVisibilityChange={handleVisibilityChange}
                />
            </div>
            <Sidebar
                display={{ base: isOpen ? 'flex' : 'none', md: 'flex' }}
                activePage={activePage}
                setActivePage={setActivePage}
                close={onClose}
                bannerHeight={
                    isBannerEnabled &&
                    !isPermanentEidAccepted &&
                    !isTemporaryEidAccepted
                        ? bannerHeight
                        : 0
                }
                squadEnforcedMode={isBannerEnabled}
            />
            {/* Mobile Nav */}
            <div className="md:hidden">
                <MobileNav
                    pageTitle={pageTitle}
                    isTopNavVisible={isLivebooks}
                    isOpen={isOpen}
                    onToggle={onOpen}
                />
            </div>
            {/* Main Content Start */}
            <main
                className={cn(
                    'flex flex-col min-h-full transition-[margin-left] duration-300 ease-in-out bg-white dark:bg-gray-900',
                    isOpen ? 'ml-[260px]' : 'ml-0',
                    'md:ml-16',
                    (isAttendance || isLivebooks) && 'pt-14 md:pt-0'
                )}
            >
                <Outlet />
            </main>
            <Notifications />
            <Toaster />
            {/* Main Content End */}
        </div>
    )
}

export default DefaultLayout
